import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Play, ShieldCheck, Sparkles, TrendingUp, MessageCircle, CheckCircle2 } from "lucide-react";
import ParticleNetwork from "@/components/site/ParticleNetwork";
import { Glow } from "@/components/site/bits";
import { scrollToSection } from "@/lib/scroll";

const WA = "https://wa.me/917042141004";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const HEADLINE = ["AI Solutions That Grow", "Your Business While You", "Focus on Growth"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCards = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (id) => scrollToSection(id);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden grid-bg" data-testid="hero">
      <div className="absolute inset-0 opacity-70">
        <ParticleNetwork />
      </div>
      <Glow className="w-[500px] h-[500px] -top-40 -left-32" color="#00E5FF" />
      <Glow className="w-[500px] h-[500px] top-40 right-0" color="#7B61FF" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-transparent to-[#0B1220]" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-6 pt-36 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-8"
        >
          <span className="h-2 w-2 rounded-full bg-[#00FFB2] animate-pulse" />
          <span className="text-xs tracking-wide text-white/80">GST Registered · AI & Digital Transformation · India</span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-5xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="inline-block">
                {i === 2 ? <span className="text-gradient">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
        >
          We build AI-powered websites, mobile apps, business automation systems, AI voice agents,
          sales portals and promotional content that help businesses scale faster.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => go("contact")}
            data-testid="hero-book-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] px-7 py-3.5 font-semibold text-[#0B1220] glow-cyan hover:scale-[1.03] transition-transform"
          >
            Book Free Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => go("portfolio")}
            data-testid="hero-portfolio-btn"
            className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 font-semibold text-white hover:border-[#00E5FF]/50 transition-colors"
          >
            <Play className="h-4 w-4 text-[#00FFB2]" /> View Portfolio
          </button>
          <a
            href={WA}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp"
            className="inline-flex items-center gap-2 rounded-full border border-[#00FFB2]/40 px-6 py-3.5 text-sm text-[#00FFB2] hover:bg-[#00FFB2]/10 transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> Quick Connect
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-6"
        >
          {["GST Registered Company", "Trusted AI Solutions", "Custom Business Development"].map((b) => (
            <div key={b} className="flex items-center gap-2 text-sm text-white/60">
              <CheckCircle2 className="h-4 w-4 text-[#00E5FF]" /> {b}
            </div>
          ))}
        </motion.div>

        {/* Floating dashboard cards */}
        <motion.div style={{ y: yCards }} className="pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -4 }}
            animate={{ opacity: 1, y: 0, rotate: -4 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="hidden xl:block absolute right-8 top-40 w-64 glass rounded-2xl p-5 animate-float"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-white/60">Revenue Growth</span>
              <TrendingUp className="h-4 w-4 text-[#00FFB2]" />
            </div>
            <div className="font-display text-3xl font-bold text-gradient">+312%</div>
            <div className="mt-4 flex items-end gap-1.5 h-14">
              {[30, 45, 38, 60, 52, 78, 95].map((h, i) => (
                <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#00E5FF]/30 to-[#00E5FF]" style={{ height: `${h}%` }} />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 5 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="hidden xl:block absolute right-40 bottom-8 w-60 glass rounded-2xl p-5"
            style={{ animationDelay: "1.5s" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-[#7B61FF]" />
              <span className="text-xs text-white/60">Automation Live</span>
            </div>
            <div className="font-display text-2xl font-bold">52,000+ hrs</div>
            <div className="text-xs text-white/50 mt-1">saved for clients</div>
            <div className="mt-3 flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-[#00FFB2]" />
              <span className="text-xs text-[#00FFB2]">Running 24/7</span>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
